import { feedPagePath } from './feedLinks';

/**
 * Where the reader's shared views (nav, feed switcher, feed chips, story
 * cards, the Feeds page) link. Every reader path serves the feeds v2 views
 * (`pages/ReaderEntryRoutes`), which provide `V2_READER_ROUTES` through
 * `ReaderRoutesProvider`, so a component never hardcodes a reader path.
 */
export interface ReaderRoutes {
  /** For You. */
  home: string;
  /** The Feeds page. */
  feeds: string;
  /** Where a new search starts. */
  newSearch: string;
  feed: (feedId: string, options?: { expanded?: boolean }) => string;
  feedExport: (feedId: string, format: 'xml' | 'md', hours: number) => string;
}

/**
 * The feeds v2 search page: `/search/<id>` for a search with a description id
 * (or an old run's id), `/search?q=` for text no search has named yet (no `q`
 * shows the empty composer).
 */
export const V2_SEARCH_PATH = '/search';

/** The search page searching `query`, over 72 hours when `hours72` (24, the page's default, otherwise). */
export function searchPagePath(query: string, hours72: boolean): string {
  return `${V2_SEARCH_PATH}?${new URLSearchParams({ q: query, ...(hours72 ? { hours: '72' } : {}) })}`;
}

/** The search page for the search `searchId` names (a description's id, or an old run's), over 72 hours when `hours72`. */
export function searchIdPath(searchId: string, hours72: boolean): string {
  return `${V2_SEARCH_PATH}/${encodeURIComponent(searchId)}${hours72 ? '?hours=72' : ''}`;
}

export const V2_READER_ROUTES: ReaderRoutes = {
  home: '/',
  feeds: '/feeds',
  newSearch: V2_SEARCH_PATH,
  feed: feedPagePath,
  feedExport: (feedId, format, hours) => `/api/v1/feeds/${encodeURIComponent(feedId)}/export.${format}?hours=${hours}`,
};

/**
 * Where `/` goes when its query string asks for a view the snapshot-served
 * Home drew (old links: digests, bookmarks, legacy redirects, a signed-out
 * composer's search), or null to show For You. `search` is the query string,
 * with its leading `?`.
 *
 * - `?interest=<feed id>` -> `{ interestId }`: the caller resolves it to a feed
 *   (an imported subscription answers to its legacy ids) and opens that feed's page.
 * - `?search=<description id>` (or `search_id`) -> `/search/<description id>`.
 * - `?q=<text>` or `?edit=1` -> the search page, with the text if any.
 * - `?run=<run id>` alone -> `/search/<run id>`, which reads the run's description.
 *
 * `hours=72` is kept on every target.
 */
export function homeLinkTarget(search: string): { to: string } | { interestId: string } | null {
  const params = new URLSearchParams(search);
  const hours = params.get('hours') === '72' ? '72' : null;
  const withHours = (path: string, query: Record<string, string> = {}) => {
    const next = new URLSearchParams({ ...query, ...(hours ? { hours } : {}) }).toString();
    return next ? `${path}?${next}` : path;
  };
  const interest = params.get('interest');
  if (interest) return { interestId: interest };
  const descriptionId = params.get('search') || params.get('search_id');
  if (descriptionId) return { to: searchIdPath(descriptionId, hours !== null) };
  const query = (params.get('q') ?? '').trim();
  if (query) return { to: withHours(V2_SEARCH_PATH, { q: query }) };
  const run = params.get('run');
  if (run) return { to: searchIdPath(run, hours !== null) };
  if (params.has('edit')) return { to: withHours(V2_SEARCH_PATH) };
  return null;
}

/** For You v2's exports: the stored builds of the feeds it shows, combined as For You combines them. */
export function forYouV2ExportLinks(feedIds: string[], hours: number): { rss: string; markdown: string } {
  const params = new URLSearchParams({ ids: feedIds.join(','), hours: String(hours) });
  return { rss: `/api/v1/feeds/view/export.xml?${params}`, markdown: `/api/v1/feeds/view/export.md?${params}` };
}
